import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  XCircle,
  AlertTriangle,
  Users,
  TrendingDown,
  RefreshCw,
  Calendar,
  Filter,
  User,
  Car,
} from "lucide-react";

export const CityAdminCancelledAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    if (user?.id) fetchAnalytics();
  }, [user?.id]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const res = await axios.get(`${API}/city-admin/${user.id}/cancelled-analytics?${params.toString()}`);
      setAnalytics(res.data);
    } catch (err) {
      toast.error("Failed to load cancellation analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="city-admin-cancelled-analytics">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <XCircle className="w-6 h-6 text-red-500" /> Cancelled Rides Analytics
          </h1>
          <p className="text-sm text-muted-foreground">Analyze cancellation patterns in your city</p>
        </div>
        <Button variant="outline" onClick={fetchAnalytics}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-sm">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm">From Date</Label>
            <div className="relative">
              <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="pl-8" />
            </div>
          </div>
          <div>
            <Label className="text-sm">To Date</Label>
            <div className="relative">
              <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="pl-8" />
            </div>
          </div>
          <div className="flex items-end">
            <Button onClick={fetchAnalytics} className="w-full">Apply Filters</Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-600">Total Cancelled</span>
          </div>
          <p className="text-3xl font-bold text-red-700">{analytics?.total_cancelled || 0}</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-600">By Customer</span>
          </div>
          <p className="text-3xl font-bold text-yellow-700">{analytics?.cancellation_by_who?.customer || 0}</p>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Car className="w-5 h-5 text-orange-600" />
            <span className="text-sm text-orange-600">By Driver</span>
          </div>
          <p className="text-3xl font-bold text-orange-700">{analytics?.cancellation_by_who?.driver || 0}</p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-purple-600">By Admin</span>
          </div>
          <p className="text-3xl font-bold text-purple-700">{analytics?.cancellation_by_who?.admin || 0}</p>
        </div>
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-600">System</span>
          </div>
          <p className="text-3xl font-bold text-gray-700">{analytics?.cancellation_by_who?.system || 0}</p>
        </div>
      </div>

      {/* Top Cancellation Reasons */}
      <div className="bg-card border rounded-lg p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-red-500" /> Top Cancellation Reasons
        </h3>
        {analytics?.top_reasons?.length > 0 ? (
          <div className="space-y-3">
            {analytics.top_reasons.map((reason, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx === 0 ? "bg-red-100 text-red-700" :
                    idx === 1 ? "bg-orange-100 text-orange-700" :
                    idx === 2 ? "bg-yellow-100 text-yellow-700" :
                    "bg-gray-100 text-gray-700"
                  }`}>{idx + 1}</span>
                  <span className="font-medium">{reason.reason}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2.5">
                    <div className="bg-red-500 h-2.5 rounded-full" style={{ width: `${Math.min(100, (reason.count / (analytics.total_cancelled || 1)) * 100)}%` }} />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">{reason.count}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">No cancellation data available</p>
        )}
      </div>

      {/* Frequent Cancellers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-yellow-500" /> Frequent Customer Cancellers
          </h3>
          {analytics?.frequent_customer_cancellers?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Cancellations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.frequent_customer_cancellers.map((customer, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.customer_name || "Unknown"}</p>
                        <p className="text-sm text-muted-foreground">{customer.customer_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        customer.count >= 5 ? "bg-red-100 text-red-700" :
                        customer.count >= 3 ? "bg-orange-100 text-orange-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>{customer.count}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-center py-4">No frequent cancellers found</p>
          )}
        </div>

        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Car className="w-5 h-5 text-orange-500" /> Drivers with Most Cancellations
          </h3>
          {analytics?.frequent_driver_cancellers?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead className="text-right">Cancellations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.frequent_driver_cancellers.map((driver, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <p className="font-medium">{driver.driver_name || "Unknown"}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        driver.count >= 5 ? "bg-red-100 text-red-700" :
                        driver.count >= 3 ? "bg-orange-100 text-orange-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>{driver.count}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-center py-4">No driver cancellations found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CityAdminCancelledAnalytics;
